<upload-box>

    <div class="dropzone-instance dropzone w-full h-48 flex items-center justify-center rounded-lg bg-gray-100 dropzone-dashed-border {props.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-[0.8]'}">
        <div class="flex flex-col w-full items-center">
            <i class="icon-metalark-upload mb-4 text-xl text-accent"></i>
            <p class="text-gray-600 text-center font-bold">
                {getDisabledMessage()}
            </p>
            <div class="flex justify-center w-full my-4 align-center items-center text-center" if="{!props.disabled}">
                <div class="border-t border-primary-500 w-6"></div>
                <span class="mx-4 text-sm font-bold">{locale?.t('or')}</span>
                <div class="border-t border-primary-500 w-6"></div>
            </div>
            <button type="button" class="btn btn-primary btn-sm rounded-lg px-8 uppercase" disabled="{props.disabled}" if="{!props.disabled}">
                <i class="icon-folder1 text-lg"></i>
                {locale?.t('browsefiles')}
            </button>
            <input id="fileInput" type="file" class="hidden" multiple disabled="{props.disabled}" aria-label="{locale?.t('browsefiles')}" />
        </div>
    </div>

    <!-- The max-files hint only matters when a real multi-file limit applies.
         In reupload mode it's always a single replacement file, so hide it
         rather than show an awkward "Max 1 Files Per Upload". -->
    <div if="{!props.resourceid}" class="text-xs block mt-2 text-primary font-semibold text-center flex flex-col xl:flex-row gap-2 justify-center">
        <span>{locale?.t('uploadbox_maxfiles', {count: effectiveMaxFiles()})}</span>
    </div>

    <style>
        /* https://kovart.github.io/dashed-border-generator/ */
        .dropzone-dashed-border {
            border: none;
            background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23A75E7DFF' stroke-width='3' stroke-dasharray='10%2c 10' stroke-dashoffset='2' stroke-linecap='round'/%3e%3c/svg%3e");
        }
        .dropzone.dz-clickable * {
            cursor: pointer;
        }
        #fileInput {
            display: none;
        }
    </style>

    <script>
        import { Dropzone } from "dropzone";
        import { values } from "../services/values"
        import { getModalService } from "../services/service-loader"

        export default {
            values,

            state: {
                maxFiles: values.maxFiles,
                uploadedFileNames: [],
                failedFileNames: [],
                invalidFileNames: []
            },

            // In reupload mode (a resourceid is supplied) the box replaces a
            // single existing resource, so only one file is accepted.
            effectiveMaxFiles() {
                return this.props.resourceid ? 1 : this.state.maxFiles;
            },

            getDisabledMessage() {
                if (!this.props.disabled) {
                    // Reupload replaces a single file, so use the singular prompt.
                    const dragKey = this.props.resourceid ? 'uploadbox_dragfile' : 'uploadbox_dragfiles';
                    return this.locale?.t(dragKey) || 'Drag files to upload';
                }

                switch (this.props.disabledReason) {
                    case 'trial-expired':
                        return this.locale?.t('uploadbox_trialexpired') || 'Trial expired - upgrade to continue';
                    case 'grace-period':
                        return this.locale?.t('uploadbox_graceperiod') || 'Subscription in grace period - renew to upload';
                    case 'subscription-expired':
                        return this.locale?.t('uploadbox_subscriptionexpired') || 'Subscription expired - renew to continue';
                    case 'quota-exhausted':
                        return this.locale?.t('uploadbox_quotaexhausted') || 'Upload limit reached - no credits remaining';
                    default:
                        return this.locale?.t('uploadbox_uploadlimitreached') || 'Upload limit reached';
                }
            },

            async onMounted() {
                this.initDropzone();
            },

            onUpdated() {
                // Reinitialize dropzone if disabled state changes
                if (this.dropzone) {
                    if (this.props.disabled) {
                        this.dropzone.disable();
                    } else {
                        this.dropzone.enable();
                    }
                }
            },

            initDropzone() {
                // Disable Dropzone auto-discovery if you want full control
                Dropzone.autoDiscover = false;

                const el = this.$('.dropzone-instance');
                const children = el.querySelectorAll("button");

                // Create a new Dropzone instance
                this.dropzone = new Dropzone(el, {
                    url: "none",
                    autoProcessQueue: false,
                    maxFilesize: 2000,
                    maxFiles: this.effectiveMaxFiles(),
                    addRemoveLinks: true,
                    dictRemoveFile: this.locale?.t('remove') || "Remove",
                    dictDefaultMessage: "",
                    previewsContainer: false,
                    clickable: this.props.disabled ? false : Array.from(children),
                    acceptedFiles: values.acceptedFiles.join(','),
                });


                // Disable immediately if props.disabled is true
                if (this.props.disabled) {
                    this.dropzone.disable();
                }

                this.dropzone.on("addedfiles", async (files) => {
                    console.log("Files added:", files);
                    const filesArray = Object.values(files);
                    if (this.props.resourceid) {
                        // Reupload mode: replace the target resource with a single
                        // file via the in-place reupload path (no pre-upload modal,
                        // no collection). The backend re-extracts the resource.
                        const file = filesArray[0];
                        if (!file) return;
                        // Step the host modal aside BEFORE any alert. The host uses
                        // showModal() (top layer), while the invalid-files / error /
                        // quota alerts open with show() (non-top-layer) and would
                        // otherwise render hidden behind it.
                        if (this.props.onstart) this.props.onstart();
                        if (!this.tools?.isValidFileType(file)) {
                            this.tools?.showInvalidFilesModal([file.name]);
                            return;
                        }
                        try {
                            await this.tools?.uploadFile(file, this.props.resourceid);
                        } catch (err) {
                            // uploadFile surfaces the quota modal itself but always
                            // rethrows; since the host modal already stepped aside,
                            // a non-quota failure would otherwise be silent. Show an
                            // error and skip the success completion below. We do NOT
                            // reopen the host modal here: it uses showModal() (top
                            // layer), which would render above the error dialog
                            // (opened with the non-top-layer show()) and hide it.
                            const message = err?.response?.data?.message || err?.message || String(err);
                            if (!this.tools?.isQuotaError(message)) {
                                const modalService = await getModalService();
                                modalService.show(
                                    this.locale?.t('reupload_failed'),
                                    this.locale?.t('reupload_failed_message')
                                );
                            }
                            return;
                        }
                    } else {
                        // Let the host modal step aside before the pre-upload
                        // dialog opens on top of it.
                        if (this.props.onstart) this.props.onstart();
                        // When this box is scoped to a collection (collectionid prop),
                        // pin every uploaded file to it and lock the collection field
                        // in the pre-upload modal so the user is not prompted to choose.
                        const collectionId = this.props.collectionid || null;
                        await this.tools?.uploadFiles(filesArray, null, collectionId, !!collectionId);
                    }
                    if (this.props.onupdated) this.props.onupdated();
                });
            }
        }
    </script>
</upload-box>